import type { HorizonKind } from "@beresta/core";
import type { IconName } from "@beresta/ui";
import type { MessageKey } from "../i18n.js";
import type { PageName, Route } from "../router/index.js";

/**
 * Пʼять місць нижньої панелі (картка TabBar): Час, Записи, Писати, Люди, Ще.
 * Кожна сторінка продукту належить рівно одному місцю — так закрито дірку,
 * через яку половина сторінок жила в коді й не мала жодної кнопки.
 *
 * Пʼять — межа: на 320 шостий підпис стає нечитним.
 */
export type Tab = "time" | "records" | "people" | "more";

export const TABS: readonly { tab: Tab | "write"; label: MessageKey; icon: IconName }[] = [
  { tab: "time", label: "tab.time", icon: "time" },
  { tab: "records", label: "tab.records", icon: "records" },
  { tab: "write", label: "tab.write", icon: "write" },
  { tab: "people", label: "tab.people", icon: "people" },
  { tab: "more", label: "tab.more", icon: "more" },
];

/** Сегменти «Записів» (F1–F4) — чотири сторінки одного екрана. */
export const RECORD_PAGES = ["feed", "notes", "inbox", "reminders"] as const satisfies readonly PageName[];

const PEOPLE_PAGES: readonly PageName[] = ["people", "groups"];

export function tabOf(route: Route): Tab {
  if (route.kind === "horizon") return "time";
  if (route.kind === "person") return "people";
  if (route.kind !== "page") return "more";
  if ((RECORD_PAGES as readonly PageName[]).includes(route.page) || route.page === "search") return "records";
  if (PEOPLE_PAGES.includes(route.page)) return "people";
  return "more";
}

/** Куди веде дотик по місцю панелі: корінь місця, стек порожній. */
export function tabRoot(tab: Tab): PageName | null {
  switch (tab) {
    case "time":
      return null;
    case "records":
      return "feed";
    case "people":
      return "people";
    case "more":
      return "more";
  }
}

/**
 * Екрани «Ще», що лежать у стеку над M1: мають «‹ Ще» у шапці. Екрани
 * третього рівня (M12 Календарі, M15 Tasks, M13 Контакти) ведуть назад
 * у M11 — глибина стеку не більша за два кроки від вкладки (WindowMap).
 */
export function parentOf(page: PageName): PageName | null {
  switch (page) {
    case "calendars":
    case "tasks":
    case "contacts":
      return "connections";
    case "groups":
      return "people";
    case "more":
    case "people":
    case "feed":
    case "notes":
    case "inbox":
    case "reminders":
      return null;
    case "search":
      return "feed";
    default:
      return "more";
  }
}

/** Масштаби, у яких є екран. Натискним показується лише те, куди можна піти. */
const SCREENED: readonly HorizonKind[] = ["year", "month", "week", "day"];

export function hasScreen(kind: HorizonKind): boolean {
  return SCREENED.includes(kind);
}
